import React, { useMemo, useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { ColDef, ColGroupDef } from "ag-grid-community";
import { RenderTable } from "../../common/splitview-table/RenderTable";
import { gridTheme } from "../../../utils/tableUtils";

export interface HistoryLogContextRow {
  Plant?: string | number;
  "IO Material Type"?: string;
  "Planning Lead"?: string;
}

export interface HistoryLogTableRow {
  __uid?: string;
  materialPlant?: string;
  ioMaterialType?: string;
  planningLead?: string | null;
  approverName: string;
  approverEmail: string;
  fromDate: string;
  toDate: string;
  updatedBy?: string;
  updatedTimestamp?: string;
}

type HistoryLogDialogProps = {
  open: boolean;
  onClose: () => void;
  row: HistoryLogContextRow | null;
  loading: boolean;
  logs: HistoryLogTableRow[];
};

const HistoryLogDialog: React.FC<HistoryLogDialogProps> = ({
  open,
  onClose,
  row,
  loading,
  logs,
}) => {
  const historyGridRef = useRef<any>(null);

  const historyColumns: (ColDef | ColGroupDef)[] = useMemo(
    () => {
      const dateSortComparator = (a: any, b: any) => {
        const toTime = (v: any) => {
          if (!v || v === "-") return 0;
          const t = new Date(v).getTime();
          return isNaN(t) ? 0 : t;
        };
        return toTime(a) - toTime(b);
      };
      return [
        { headerName: "Approver Name", field: "approverName" },
        { headerName: "Approver Email", field: "approverEmail" },
        { headerName: "From Date", field: "fromDate", comparator: dateSortComparator },
        { headerName: "To Date", field: "toDate", comparator: dateSortComparator },
        { headerName: "Created/Changed By", field: "updatedBy" },
        { headerName: "Created/Changed On Timestamp", field: "updatedTimestamp", comparator: dateSortComparator },
      ];
    },
    [],
  );

  return (
    <Dialog open={ open } onClose={ onClose } fullWidth maxWidth="md">
      <DialogTitle>History Log</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={ 2 } sx={ { mb: 2, flexWrap: "wrap" } }>
          <Typography variant="body2">Plant: { row?.Plant || "-" }</Typography>
          <Typography variant="body2">
            Material Type: { row?.["IO Material Type"] || "-" }
          </Typography>
          <Typography variant="body2">
            Planning Lead: { row?.["Planning Lead"] || "-" }
          </Typography>
        </Stack>

        <RenderTable
          loading={ loading }
          gridRef={ historyGridRef }
          gridTheme={ gridTheme }
          columns={ historyColumns }
          rows={ logs ?? [] }
          rowsPerPage={ 5 }
          resizable={ true }
          setGridReady={ () => { } }
          sideBar={ {} }
          minimized={ null }
          emitEvent={ () => { } }
          onChange={ () => { } }
          isSelectedRow={ false }
          setSelectedRow={ () => { } }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={ onClose } color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoryLogDialog;
